import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, FieldProps, Form, Formik, FormikValues } from 'formik';
import { useTranslation } from 'react-i18next';

import * as FormikUtils from '../../../utils/formik';
import * as ProjectSettingsUtils from '../../../utils/projectSettings';
import { testProjectSchema } from '../../../utils/validation/schemas';
import { formSteps, projectSettingsSteps } from './assets';

import { Wrapper } from './TestProjectForm.styled';
import { useDispatch, useSelector } from 'react-redux';
import { projectSettingsActions } from '../../../redux/project-settings/actions';
import { selectLanguage } from '../../../redux/app/selectors';
import { selectData, selectUI } from '../../../redux/project-settings/selectors';

const initValues = {
  domain: '',
  name: '',
  strategy: '',
  recrawl_interval: null,
  urls: [],
  url_groups: [],
  invites: [],
  goals: [],
  is_warmup_urls: false,
};

export const TestProjectFormBag = FormikUtils.createFormBag();

export interface IProps {
  onSubmit: (values: FormikValues) => void;
}

const TestProjectForm: React.FC<IProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lang = useSelector(selectLanguage);
  const data = useSelector(selectData);
  const { isNew } = useSelector(selectUI);
  const [mounted, setMounted] = useState(false);
  const [initErrors, setInitErrors] = useState<any>({});

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const finishedSteps: number[] = mounted ? ProjectSettingsUtils.createFinishedSteps(formSteps, TestProjectFormBag.errors, TestProjectFormBag.touched, TestProjectFormBag.values) : [];
    dispatch(projectSettingsActions.finishedStepsRefresh(finishedSteps));
  }, [data, TestProjectFormBag.values, TestProjectFormBag.touched, TestProjectFormBag.errors]);

  useEffect(() => {
    TestProjectFormBag.validateForm().then((result: any) => {
      setInitErrors(result);
    });
  }, []);

  const onBlur = useCallback((e) => {
    dispatch(projectSettingsActions.dataRefresh({ [e.target.name]: e.target.value }));
  }, [TestProjectFormBag.touched, TestProjectFormBag.values, TestProjectFormBag.errors, mounted]);

  const initTouched = useMemo(() => {
    return !isNew ? Object.keys(initValues).reduce((acc, key) => ({ ...acc, [key]: true }), {}) : {};
  }, [isNew]);

  return (
    <Wrapper>
      <Formik
        initialValues={data}
        initialTouched={initTouched}
        initialErrors={initErrors}
        validationSchema={testProjectSchema}
        validateOnBlur
        onSubmit={onSubmit}
      >
        {(formikProps) => {
          FormikUtils.fillFormBag(TestProjectFormBag, formikProps);

          return (
            <Form>
              {projectSettingsSteps.map(({ title, suffix = '', fields, id, className }) =>
                <React.Fragment key={id}>
                  <div id={id} className="fields-group-title">
                    <span>{t(title)}</span>
                    {suffix && <span className="suffix">({t(suffix)})</span>}
                  </div>
                  <div className={className}>
                    {fields.map(fieldConfig => {
                      const { name } = fieldConfig;
                      const dynamicFieldConfig: Record<string, unknown> = { ...fieldConfig, onBlur: onBlur };

                      return (
                        <Field name={name} key={name}>
                          {(fieldProps: FieldProps) => FormikUtils.createField(fieldProps, dynamicFieldConfig)}
                        </Field>
                      );
                    })}
                  </div>
                </React.Fragment>
              )}
            </Form>
          );
        }}
      </Formik>
    </Wrapper>
  );
}

export default TestProjectForm;